import React, { useEffect, useState, ReactNode } from 'react';
import { User, LoginCredentials, RegisterData } from '../types';
import { AuthContext, type AuthContextType } from './authContextValue';
import { apiService } from '../services/api';
import { getErrorMessage, getErrorStatus } from '../utils/errors';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Load initial user from localStorage
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        console.error('Failed to parse saved user:', e);
        return null;
      }
    }
    return null;
  });
  
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // ⚠️ `isLoading` gehoert dem BOOTSTRAP: „die App prueft beim Start, ob jemand angemeldet
  // ist". `PublicRoute` in App.tsx tauscht bei `isLoading` die ganze Seite gegen einen
  // Spinner — haengt also die Login-Seite AUS.
  //
  // Genau daran ist die Fehlermeldung gestorben: der Anmeldevorgang setzte dasselbe Flag,
  // die Seite verschwand waehrend des Versuchs, und der `catch` schrieb sein `setError` in
  // eine Komponente, die im selben Wimpernschlag ausgehaengt wurde. Beim Zurueckkommen war
  // sie frisch — mit leerem Fehler. Auf dem Bildschirm blieb es still, in der Konsole stand
  // der Fehler brav.
  //
  // Ein laufender Anmeldeversuch ist deshalb KEIN globaler Ladezustand. Den Knopf sperrt die
  // Login-Seite ueber ihr eigenes `isLoading`.
  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('🔐 Attempting login via API...');
      const response = await apiService.login(credentials);
      setUser(response.user);
      // Save to localStorage for persistence
      localStorage.setItem('user', JSON.stringify(response.user));
      console.log('✅ Login successful, user:', response.user.email);
    } catch (error: unknown) {
      console.error('❌ Login failed:', error);
      throw new Error(getErrorMessage(error, 'Login fehlgeschlagen'));
    }
  };

  // Dieselbe Kopplung wie bei `login` — aus demselben Grund geloest.
  const register = async (data: RegisterData) => {
    try {
      const response = await apiService.register(data);
      setUser(response.user);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout(); // supabase.auth.signOut()
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      const status = getErrorStatus(error);

      // ⛔ ERLAUBNISLISTE, kein Sonderfall: NUR eine Antwort, die „du bist nicht angemeldet"
      // BEDEUTET, darf den Nutzer vergessen lassen. Alles andere — 429, 5xx, ein Netzfehler, ein
      // abgebrochener Wechsel — heisst „der Server hat gerade nein gesagt" und laesst den
      // Anmeldezustand stehen.
      //
      // ⚠️ Hier stand bis 2026-09-04 ein blankes `setUser(null)` im `catch`. Es unterschied die
      // beiden Bedeutungen nicht. Der Betreiber lief am 2026-09-04 beim Massen-Veroeffentlichen in
      // den Mengenbegrenzer, `GET /api/users/me` bekam 429, und die Anwendung warf ihn auf
      // `/login` — ueber `isAuthenticated = !!user` und den `ProtectedRoute`. Er war dabei NIE
      // abgemeldet: die Supabase-Sitzung hat ueberlebt, die Anwendung hatte ihn nur vergessen.
      //
      // ⛔ Nicht als `if (status === 429)` bauen. Wer nur den einen Fall ausnimmt, hat beim
      // naechsten 503 denselben Fehler. Auch `undefined` (Netzfehler, kein Statuscode) faellt
      // bewusst auf „Anmeldezustand behalten".
      //
      // 403 gehoert AUSDRUECKLICH nicht dazu: „angemeldet, aber nicht erlaubt" ist kein Grund,
      // jemanden abzumelden (siehe die 401/403-Trennung in `AuthMiddleware.ts`).
      if (status === 401) {
        setUser(null);
        localStorage.removeItem('user');
        return;
      }

      console.error('Benutzerdaten konnten nicht aufgefrischt werden; Anmeldung bleibt bestehen:', error);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Source of truth = the Supabase session (not a stale localStorage token).
        if (await apiService.isAuthenticated()) {
          await refreshUser();
        } else {
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
